import { Button, Input } from "@aix/ui"
import { CheckCircle2, Cog, Copy, KeyRound, Loader2, QrCode, RefreshCw, ShieldCheck, Trash2, User } from "lucide-react"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"

import { useAuth } from "../lib/auth-context"
import { isPasskeyEnabled, supabase } from "../lib/supabase"
import { AccessTokensPanel } from "../components/AccessTokensPanel"

const MIN_PASSWORD_LENGTH = 8
const MFA_CODE_LENGTH = 6

type TabType = "profile" | "security" | "access-tokens" | "preferences"
type TotpFactor = {
  id: string
  friendly_name?: string
  status: "verified" | "unverified"
  created_at: string
}

type PendingTotpEnrollment = {
  factorId: string
  qrCode: string
  secret: string
}

type Passkey = {
  id: string
  friendly_name?: string
  created_at: string
  last_used_at?: string
}

function getAuthErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback
}

function getQrCodeDataUrl(qrCode: string) {
  const svgDataPrefix = "data:image/svg+xml;utf-8,"
  const svgBase64Prefix = "data:image/svg+xml;base64,"

  if (qrCode.startsWith(svgBase64Prefix)) {
    return qrCode
  }

  const svgMarkup = qrCode.startsWith(svgDataPrefix) ? qrCode.slice(svgDataPrefix.length) : qrCode
  return `${svgDataPrefix}${encodeURIComponent(svgMarkup)}`
}

export default function SettingsScreen() {
  const [activeTab, setActiveTab] = useState<TabType>("profile")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [savingPassword, setSavingPassword] = useState(false)
  const [mfaFactors, setMfaFactors] = useState<TotpFactor[]>([])
  const [pendingEnrollment, setPendingEnrollment] = useState<PendingTotpEnrollment | null>(null)
  const [mfaFriendlyName, setMfaFriendlyName] = useState("Authenticator app")
  const [mfaCode, setMfaCode] = useState("")
  const [loadingMfa, setLoadingMfa] = useState(false)
  const [startingMfaEnrollment, setStartingMfaEnrollment] = useState(false)
  const [verifyingMfa, setVerifyingMfa] = useState(false)
  const [removingFactorId, setRemovingFactorId] = useState<string | null>(null)
  const [passkeys, setPasskeys] = useState<Passkey[]>([])
  const [loadingPasskeys, setLoadingPasskeys] = useState(false)
  const [registeringPasskey, setRegisteringPasskey] = useState(false)
  const [removingPasskeyId, setRemovingPasskeyId] = useState<string | null>(null)
  const { updatePassword, user } = useAuth()

  const verifiedMfaFactors = useMemo(() => mfaFactors.filter((factor) => factor.status === "verified"), [mfaFactors])
  const pendingQrDataUrl = pendingEnrollment ? getQrCodeDataUrl(pendingEnrollment.qrCode) : null

  const tabs = [
    { id: "profile" as const, label: "Profile", icon: User },
    { id: "security" as const, label: "Security", icon: ShieldCheck },
    { id: "access-tokens" as const, label: "Access tokens", icon: KeyRound },
    { id: "preferences" as const, label: "Preferences", icon: Cog },
  ]

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
      return
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match")
      return
    }

    setSavingPassword(true)
    const result = await updatePassword(password)
    setSavingPassword(false)

    if (result.error) {
      toast.error(result.error)
      return
    }

    setPassword("")
    setConfirmPassword("")
    toast.success("Password updated")
  }

  const loadMfaFactors = useCallback(async () => {
    setLoadingMfa(true)
    try {
      const { data, error } = await supabase.auth.mfa.listFactors()

      if (error) {
        toast.error(error.message)
        return
      }

      setMfaFactors(data.totp)
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Failed to load MFA settings"))
    } finally {
      setLoadingMfa(false)
    }
  }, [])

  const handleStartMfaEnrollment = async () => {
    const friendlyName = mfaFriendlyName.trim()

    if (!friendlyName) {
      toast.error("Name this MFA method first")
      return
    }

    setStartingMfaEnrollment(true)
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName,
        issuer: "aixlabs",
      })

      if (error) {
        toast.error(error.message)
        return
      }

      setPendingEnrollment({
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
      })
      setMfaCode("")
      setMfaFriendlyName("Authenticator app")
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Failed to start MFA enrollment"))
    } finally {
      setStartingMfaEnrollment(false)
    }
  }

  const handleVerifyMfaEnrollment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!pendingEnrollment) {
      toast.error("Start MFA enrollment first")
      return
    }

    if (!/^\d{6}$/.test(mfaCode)) {
      toast.error(`Enter the ${MFA_CODE_LENGTH}-digit code from your authenticator app`)
      return
    }

    setVerifyingMfa(true)
    try {
      const challengeResult = await supabase.auth.mfa.challenge({ factorId: pendingEnrollment.factorId })

      if (challengeResult.error) {
        toast.error(challengeResult.error.message)
        return
      }

      const verifyResult = await supabase.auth.mfa.verify({
        factorId: pendingEnrollment.factorId,
        challengeId: challengeResult.data.id,
        code: mfaCode,
      })

      if (verifyResult.error) {
        toast.error(verifyResult.error.message)
        return
      }

      setPendingEnrollment(null)
      setMfaCode("")
      toast.success("MFA enabled")
      await loadMfaFactors()
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Failed to verify MFA code"))
    } finally {
      setVerifyingMfa(false)
    }
  }

  const handleCopyMfaSecret = async () => {
    if (!pendingEnrollment) {
      return
    }

    try {
      await navigator.clipboard.writeText(pendingEnrollment.secret)
      toast.success("Secret copied")
    } catch {
      toast.error("Could not copy secret")
    }
  }

  const handleCancelMfaEnrollment = async () => {
    if (!pendingEnrollment) {
      return
    }

    setRemovingFactorId(pendingEnrollment.factorId)
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: pendingEnrollment.factorId })

      if (error) {
        toast.error(error.message)
        return
      }

      setPendingEnrollment(null)
      setMfaCode("")
      await loadMfaFactors()
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Failed to cancel MFA enrollment"))
    } finally {
      setRemovingFactorId(null)
    }
  }

  const handleRemoveMfaFactor = async (factorId: string) => {
    setRemovingFactorId(factorId)
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId })

      if (error) {
        toast.error(error.message)
        return
      }

      toast.success("MFA factor removed")
      await loadMfaFactors()
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Failed to remove MFA factor"))
    } finally {
      setRemovingFactorId(null)
    }
  }

  const loadPasskeys = useCallback(async () => {
    setLoadingPasskeys(true)
    try {
      const { data, error } = await supabase.auth.passkey.list()
      if (error) throw error
      setPasskeys(data ?? [])
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Failed to load passkeys"))
    } finally {
      setLoadingPasskeys(false)
    }
  }, [])

  const handleRegisterPasskey = async () => {
    setRegisteringPasskey(true)
    try {
      const { error } = await supabase.auth.registerPasskey()
      if (error) throw error
      toast.success("Passkey added")
      await loadPasskeys()
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Could not add passkey"))
    } finally {
      setRegisteringPasskey(false)
    }
  }

  const handleRemovePasskey = async (passkeyId: string) => {
    setRemovingPasskeyId(passkeyId)
    try {
      const { error } = await supabase.auth.passkey.delete({ passkeyId })
      if (error) throw error
      toast.success("Passkey removed")
      await loadPasskeys()
    } catch (error) {
      toast.error(getAuthErrorMessage(error, "Could not remove passkey"))
    } finally {
      setRemovingPasskeyId(null)
    }
  }

  useEffect(() => {
    if (activeTab === "security") {
      void loadMfaFactors()
      if (isPasskeyEnabled) void loadPasskeys()
    }
  }, [activeTab, loadMfaFactors, loadPasskeys])

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-5xl px-4 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold">Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your account settings and preferences.</p>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          {/* Sidebar Navigation */}
          <div className="w-full border border-border bg-card p-3 lg:h-fit lg:w-64">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-left ${
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 border border-border bg-card">
            {activeTab === "profile" && (
              <div className="p-6">
                <h2 className="font-heading text-2xl font-semibold mb-2">Profile</h2>
                <div className="space-y-1 text-sm">
                  <p className="text-muted-foreground">Signed in as</p>
                  <p className="font-medium text-foreground">{user?.email ?? "Unknown user"}</p>
                </div>
              </div>
            )}

            {activeTab === "security" && (
              <div className="p-6">
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <KeyRound className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-semibold">Security</h2>
                    <p className="text-sm text-muted-foreground">Manage password and two-factor authentication.</p>
                  </div>
                </div>

                <form onSubmit={handlePasswordSubmit} className="max-w-md space-y-5 border-b border-border pb-8">
                  <div className="space-y-2">
                    <label htmlFor="new-password" className="text-sm font-medium text-foreground">
                      New password
                    </label>
                    <Input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="At least 8 characters"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="confirm-password" className="text-sm font-medium text-foreground">
                      Confirm password
                    </label>
                    <Input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      placeholder="Re-enter password"
                      required
                    />
                  </div>

                  <Button type="submit" loading={savingPassword}>
                    Update password
                  </Button>
                </form>

                {isPasskeyEnabled && (
                  <div className="mt-8 space-y-4 border-b border-border pb-8">
                    <div>
                      <h3 className="font-heading text-lg font-semibold">Passkeys</h3>
                      <p className="text-sm text-muted-foreground">
                        Sign in with 1Password, Touch ID, or a security key. Your workspace will still ask for your
                        authenticator code.
                      </p>
                    </div>
                    {loadingPasskeys ? (
                      <p className="text-sm text-muted-foreground">Loading passkeys...</p>
                    ) : (
                      passkeys.map((passkey) => (
                        <div
                          key={passkey.id}
                          className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
                        >
                          <div>
                            <p className="text-sm font-medium">{passkey.friendly_name || "Passkey"}</p>
                            <p className="text-xs text-muted-foreground">
                              Added {new Date(passkey.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            loading={removingPasskeyId === passkey.id}
                            onClick={() => void handleRemovePasskey(passkey.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Remove
                          </Button>
                        </div>
                      ))
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      loading={registeringPasskey}
                      disabled={!window.isSecureContext || typeof PublicKeyCredential === "undefined"}
                      onClick={() => void handleRegisterPasskey()}
                    >
                      <KeyRound className="mr-2 h-4 w-4" /> Add passkey
                    </Button>
                  </div>
                )}

                <div className="mt-8 space-y-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="font-heading text-lg font-semibold">Authenticator app</h3>
                      <p className="text-sm text-muted-foreground">
                        Use a rotating six-digit code for sensitive actions and sign-in challenges.
                      </p>
                    </div>
                    <Button type="button" variant="outline" onClick={() => void loadMfaFactors()} disabled={loadingMfa}>
                      <RefreshCw className={`mr-2 h-4 w-4 ${loadingMfa ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    {loadingMfa ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading MFA settings
                      </div>
                    ) : verifiedMfaFactors.length > 0 ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          MFA enabled
                        </div>
                        {verifiedMfaFactors.map((factor) => (
                          <div
                            key={factor.id}
                            className="flex flex-col gap-3 rounded-md border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div>
                              <p className="text-sm font-medium">{factor.friendly_name ?? "Authenticator app"}</p>
                              <p className="text-xs text-muted-foreground">
                                Added {new Date(factor.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => void handleRemoveMfaFactor(factor.id)}
                              loading={removingFactorId === factor.id}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove
                            </Button>
                          </div>
                        ))}
                        <div className="flex flex-col gap-3 rounded-md border border-dashed border-border bg-background p-3 sm:flex-row sm:items-end sm:justify-between">
                          <div className="w-full space-y-2 sm:max-w-xs">
                            <label htmlFor="mfa-friendly-name-existing" className="text-sm font-medium text-foreground">
                              New MFA name
                            </label>
                            <Input
                              id="mfa-friendly-name-existing"
                              value={mfaFriendlyName}
                              onChange={(event) => setMfaFriendlyName(event.target.value)}
                              placeholder="Work phone, 1Password, YubiKey backup"
                              disabled={Boolean(pendingEnrollment)}
                            />
                          </div>
                          <Button
                            type="button"
                            onClick={() => void handleStartMfaEnrollment()}
                            loading={startingMfaEnrollment}
                            disabled={Boolean(pendingEnrollment)}
                          >
                            <QrCode className="mr-2 h-4 w-4" />
                            Add MFA method
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-foreground">MFA is not enabled</p>
                          <p className="text-sm text-muted-foreground">
                            Add Google Authenticator, Authy, 1Password, or Apple Passwords.
                          </p>
                          <div className="space-y-2 sm:max-w-xs">
                            <label htmlFor="mfa-friendly-name" className="text-sm font-medium text-foreground">
                              MFA name
                            </label>
                            <Input
                              id="mfa-friendly-name"
                              value={mfaFriendlyName}
                              onChange={(event) => setMfaFriendlyName(event.target.value)}
                              placeholder="Work phone, 1Password, YubiKey backup"
                              disabled={Boolean(pendingEnrollment)}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          onClick={() => void handleStartMfaEnrollment()}
                          loading={startingMfaEnrollment}
                          disabled={Boolean(pendingEnrollment)}
                        >
                          <QrCode className="mr-2 h-4 w-4" />
                          Set up MFA
                        </Button>
                      </div>
                    )}
                  </div>

                  {pendingEnrollment && (
                    <div className="rounded-md border border-border bg-background p-4">
                      <div className="grid gap-5 lg:grid-cols-[220px_1fr]">
                        {pendingQrDataUrl && (
                          <div className="flex items-center justify-center rounded-md border border-border bg-card p-4">
                            <img src={pendingQrDataUrl} alt="Authenticator app QR code" className="h-44 w-44" />
                          </div>
                        )}

                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-foreground">Scan and verify</h4>
                            <p className="text-sm text-muted-foreground">
                              Scan this QR code in your authenticator app, then enter the current six-digit code.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <label htmlFor="mfa-secret" className="text-sm font-medium text-foreground">
                              Setup key
                            </label>
                            <div className="flex gap-2">
                              <Input id="mfa-secret" type="password" value={pendingEnrollment.secret} readOnly />
                              <Button type="button" variant="outline" onClick={() => void handleCopyMfaSecret()}>
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <form onSubmit={handleVerifyMfaEnrollment} className="flex flex-col gap-3 sm:flex-row">
                            <Input
                              inputMode="numeric"
                              type="text"
                              name="one-time-code"
                              autoComplete="one-time-code"
                              pattern="[0-9]*"
                              maxLength={MFA_CODE_LENGTH}
                              value={mfaCode}
                              onChange={(event) =>
                                setMfaCode(event.target.value.replace(/\D/g, "").slice(0, MFA_CODE_LENGTH))
                              }
                              placeholder="123456"
                              aria-label="Authenticator code"
                              required
                            />
                            <Button type="submit" loading={verifyingMfa}>
                              Verify
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => void handleCancelMfaEnrollment()}
                              loading={removingFactorId === pendingEnrollment.factorId}
                            >
                              Cancel
                            </Button>
                          </form>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "preferences" && (
              <div className="p-6">
                <h2 className="font-heading text-2xl font-semibold mb-4">Preferences</h2>
                <p className="text-muted-foreground">Preference settings coming soon.</p>
              </div>
            )}

            {activeTab === "access-tokens" && <AccessTokensPanel />}
          </div>
        </div>
      </div>
    </div>
  )
}
